/**
 * BreakStatusBanner — lo que ve el barbero mientras está en una pausa temporal.
 *
 * Petición del cliente (18-08-2026, issue #18): "el barbero no ve cuánto tiempo
 * le queda para comer en ninguna parte". Hasta ahora solo veía el botón grande
 * "NO DISPONIBLE", que además mentía: en una pausa temporal SÍ es reservable.
 *
 * Muestra motivo, cuenta atrás en tiempo real hasta `breakExpectedEndAt`, hora de
 * vuelta, y dos acciones: "Ya he vuelto" (vuelve a disponible ahora) y "+15 min"
 * (amplía la pausa). Cuando la hora ya pasó, avisa de que el sistema le devuelve
 * a disponible solo (cron checkBarberBreaks) y deja el botón por si quiere
 * adelantarlo.
 */
import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { Coffee, CheckCircle, Loader2, PlusCircle } from 'lucide-react';
import { functions } from '@/lib/firebase';
import { toast } from '@/lib/toast';
import { BREAK_REASON_LABELS, type BarberStatus, type BreakReason } from '@/types';
import {
  isBreakActive,
  isBreakScheduled,
  breakStartLabel,
  breakEndLabel,
  pendingBreakSeconds,
} from '@/lib/breaks';

interface BreakStatusBannerProps {
  barberId: string;
  status: BarberStatus;
}

/**
 * Segundos de pausa que le quedan por delante.
 *
 * Mientras la pausa está solo programada, cuenta su duración íntegra: el
 * contador no arranca hasta que el profesional se sienta a comer. Ya en curso,
 * cuenta lo que falta para la hora de vuelta. Devuelve un valor <= 0 cuando esa
 * hora ya pasó, que es la señal de «vencido».
 */
function segundosRestantes(status: BarberStatus, nowMs: number): number | null {
  if (!status.breakExpectedEndAt) return null;
  if (isBreakScheduled(status, nowMs) || isBreakActive(status, nowMs)) {
    return pendingBreakSeconds(status, nowMs);
  }
  return Math.round((status.breakExpectedEndAt.toMillis() - nowMs) / 1000);
}

function formatoMMSS(totalSeconds: number): string {
  const s = Math.max(0, totalSeconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(sec).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function BreakStatusBanner({ barberId, status }: BreakStatusBannerProps) {
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState<'volver' | 'ampliar' | null>(null);

  // Cuenta atrás en tiempo real: un tick por segundo mientras el banner está montado.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const restantes = segundosRestantes(status, now);
  const vencido = restantes !== null && restantes <= 0;
  const motivo = BREAK_REASON_LABELS[status.breakReason as BreakReason] || 'En pausa';
  const horaVuelta = breakEndLabel(status);
  // #22: mientras haya cola por delante la pausa está comprometida pero no ha
  // empezado. Se muestra a qué hora para, no una cuenta atrás que no ha arrancado.
  const programada = isBreakScheduled(status, now);
  const enCurso = isBreakActive(status, now);
  const horaInicio = breakStartLabel(status, now);

  const llamar = async (accion: 'volver' | 'ampliar') => {
    if (busy) return;
    setBusy(accion);
    try {
      const setBarberAvailability = httpsCallable(functions, 'setBarberAvailability');
      if (accion === 'volver') {
        await setBarberAvailability({ barberId, status: 'available' });
        toast.success('¡Estás disponible!');
      } else {
        // Se envía la ampliación, no la duración total. Desde #22 la hora de
        // vuelta puede incluir el tramo de cola que aún queda por delante, de
        // modo que reconstruirla desde el cliente inflaba la pausa (#23).
        await setBarberAvailability({
          barberId,
          status: 'on_break',
          reason: status.breakReason,
          extendMinutes: 15,
        });
        toast.success('Pausa ampliada 15 minutos');
      }
    } catch (error) {
      console.error('Error updating break:', error);
      // El servidor rechaza ampliar una pausa que acaba de vencer. Decirlo se
      // entiende mejor que un fallo genérico, porque la acción correcta es
      // distinta: no reintentar, sino volver a marcar la pausa.
      const code = (error as { code?: string } | null)?.code;
      toast.error(
        code === 'functions/failed-precondition'
          ? 'Tu pausa ya había terminado. Vuelve a marcarla si la necesitas.'
          : 'No se pudo actualizar la pausa'
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    // El contador queda FUERA de la región viva: un `aria-live` alrededor de una
    // cifra que cambia cada segundo hace que el lector de pantalla recite
    // «44:59, 44:58, 44:57…» durante los tres cuartos de hora que dura la pausa.
    // Se anuncia el cambio de estado, no el paso del tiempo.
    <div
      className="mb-6 p-5 rounded-xl border border-warning/40 bg-warning/10"
      data-testid="break-status-banner"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-full bg-warning/15">
            <Coffee className="w-7 h-7 text-warning" aria-hidden="true" />
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-warning" role="status">
              {enCurso ? 'En pausa' : 'Pausa programada'} · {motivo}
            </p>
            {programada ? (
              <p className="text-lg font-semibold text-ink">
                Comes a las {horaInicio}, al terminar la cola
              </p>
            ) : vencido ? (
              <p className="text-lg font-semibold text-ink">
                Tu descanso ha terminado: vuelves a estar disponible
              </p>
            ) : restantes !== null ? (
              <p
                className="text-3xl font-semibold text-ink tabular-nums"
                data-testid="break-countdown"
                aria-live="off"
              >
                {formatoMMSS(restantes)}
              </p>
            ) : (
              <p className="text-lg font-semibold text-ink">Sin hora de vuelta</p>
            )}
            <p className="text-sm text-ink-muted">
              {programada
                ? `Hasta las ${horaVuelta}. Tus clientes no ven «${motivo.toLowerCase()}» todavía: ven la hora a la que paras y el tiempo de espera ya incluye el descanso.`
                : vencido
                  ? 'El sistema te devuelve a disponible en menos de un minuto; también puedes hacerlo ya.'
                  : horaVuelta
                    ? `Vuelves a las ${horaVuelta}. Tus clientes pueden seguir cogiendo turno: la espera que ven incluye este descanso.`
                    : 'Tus clientes pueden seguir cogiendo turno.'}
            </p>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {!vencido && (
            <button
              type="button"
              onClick={() => llamar('ampliar')}
              disabled={busy !== null}
              className="inline-flex items-center gap-2 px-4 py-3 rounded-lg border border-hairline bg-surface-2 hover:bg-surface-3 text-ink font-medium disabled:opacity-50"
            >
              {busy === 'ampliar'
                ? <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                : <PlusCircle className="w-5 h-5" aria-hidden="true" />}
              +15 min
            </button>
          )}
          <button
            type="button"
            onClick={() => llamar('volver')}
            disabled={busy !== null}
            className="inline-flex items-center gap-2 px-4 py-3 rounded-lg bg-accent text-on-accent hover:bg-accent-hover font-bold disabled:opacity-50"
          >
            {busy === 'volver'
              ? <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
              : <CheckCircle className="w-5 h-5" aria-hidden="true" />}
            Ya he vuelto
          </button>
        </div>
      </div>
    </div>
  );
}
